import React from "react";
import PropTypes from "prop-types";
import axios from "axios";
import lodash from "lodash";
import classNames from "classnames";
import ReactTooltip from "react-tooltip";

const CommentForm = ({ children, comment, loading, onChange, onSubmit }) => (
  <form onSubmit={onSubmit}>
    <div className="row">
      <div className="col-xs-12 col-md-9">
        <label htmlFor="comment-field" className="sr-only">
          Add Comment
        </label>
        <input
          id="comment-field"
          className="form-control"
          placeholder="Add comment... (Press enter to submit)"
          type="text"
          disabled={loading}
          value={comment}
          onChange={onChange}
          aria-label="Comment Input Field"
        />
      </div>
      <div className="col-xs-12 col-md-3">{children}</div>
    </div>
  </form>
);

class EditComment extends React.Component {
  state = {
    commentBody: this.props.comment.body,
    loading: false
  };

  render() {
    const { commentBody, loading } = this.state;

    return (
      <div>
        <CommentForm
          loading={loading}
          comment={commentBody}
          onChange={this.handlerChangeComment}
          onSubmit={this.handlerUpdate}
        >
          <div>
            <a
              className={classNames("btn btn-indigo btn-raised", {
                disabled: !this.allowToUpdate() || loading
              })}
              onClick={this.handlerUpdate}
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && this.handlerUpdate(e)}
            >
              SAVE
            </a>
            &nbsp;
            <a
              className={classNames("btn btn-text", { disabled: loading })}
              onClick={this.handlerToggle}
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && this.handlerToggle(e)}
            >
              CANCEL
            </a>
          </div>
        </CommentForm>
        <hr />
      </div>
    );
  }

  allowToUpdate = () => this.state.commentBody.length > 0;
  handlerChangeComment = e => this.setState({ commentBody: e.target.value });
  handlerToggle = () => this.props.onCancel(this.props.comment.id);

  handlerUpdate = e => {
    e.preventDefault();
    if (!this.allowToUpdate() || this.state.loading) {
      return;
    }

    this.setState({ loading: true });

    axios
      .put(`/comments/${this.props.comment.id}`, {
        comment: {
          body: this.state.commentBody
        }
      })
      .then(({ data }) => {
        toastr.success("Comment updated");
        this.setState({ loading: false, commentBody: "" }, () => {
          this.props.onUpdated(data);
        });
      })
      .catch(() => {
        toastr.error("Comment not updated");
        this.setState({ loading: false });
      });
  };
}

class NewComment extends React.Component {
  state = {
    newComment: "",
    loading: false
  };

  render() {
    const { newComment, loading } = this.state;

    return (
      <CommentForm
        comment={newComment}
        loading={loading}
        onChange={this.handleChange}
        onSubmit={this.addComment}
      >
        <button
          className={classNames("btn btn-indigo btn-raised", {
            disabled: !this.allowToCreate() || loading
          })}
          onClick={this.addComment}
          style={{
            width: "100%",
            color: (!this.allowToCreate() || loading) ? "black" : "white",
            backgroundColor: (!this.allowToCreate() || loading) ? "#A8C3FF" : "#3a4592",
          }}
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && this.addComment()}
        >
          COMMENT
        </button>
      </CommentForm>
    );
  }

  allowToCreate = () => this.state.newComment.length > 0;

  handleChange = e => this.setState({ newComment: e.target.value });

  addComment = e => {
    e.preventDefault();
    if (!this.allowToCreate() || this.state.loading) {
      return;
    }

    this.setState({ loading: true });

    axios
      .post(this.props.postUrl, {
        comment: {
          user_id: this.props.currentUserId,
          body: this.state.newComment
        }
      })
      .then(({ data }) => {
        toastr.success("Comment added");
        this.setState({ loading: false, newComment: "" });
        this.props.onAdded(data);
      })
      .catch(() => {
        toastr.error("Comment not added");
        this.setState({ loading: false });
      });
  };
}

const Item = ({ currentUserId, comment, onEditComment, onDeleteComment }) => {
  const createdAtToolTip = moment(comment.created_at).format(
    "dddd, MMMM Do YYYY, h:mm:ss a"
  );

  return (
    <span>
      <p className="grey-text">{comment.body}</p>
      <span className="grey-text small-text">
        <a
          href={`/users/${comment.user.id}`}
          className="styled-link margin-right-five"
        >
          {comment.user.full_name_reverse}
        </a>
        <span className="margin-right-five" data-tip={createdAtToolTip}>
          {moment(comment.created_at).fromNow()}
          <ReactTooltip />
        </span>

        {currentUserId === comment.user_id ? (
          <span>
            <span className="margin-right-five">•</span>
            <a
              onClick={() => onEditComment(comment.id)}
              className="styled-link pointer-hand small-text"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && onEditComment(comment.id)}
            >
              Edit
            </a>
            &nbsp;
            <span className="margin-right-five">•</span>
            <a
              onClick={() => onDeleteComment(comment.id)}
              className="styled-link pointer-hand small-text"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && onDeleteComment(comment.id)}
            >
              Delete
            </a>
          </span>
        ) : null}

        <br />
        <hr />
      </span>
    </span>
  );
};

const List = ({
  currentUserId,
  comments,
  onEditComment,
  onDeleteComment,
  onUpdated
}) => (
  <div>
    {comments.map(comment =>
      comment.edit ? (
        <EditComment
          key={comment.id}
          comment={comment}
          onCancel={onEditComment}
          onUpdated={onUpdated}
        />
      ) : (
        <Item
          key={comment.id}
          currentUserId={currentUserId}
          comment={comment}
          onEditComment={onEditComment}
          onDeleteComment={onDeleteComment}
        />
      )
    )}
  </div>
);

class Comments extends React.Component {
  state = { comments: this.props.comments };

  deleteComment = commentId => {
    var that = this;

    if (window.confirm("Are you sure you want to delete this comment?")) {
      var comments = lodash.reject(this.state.comments, { id: commentId });

      axios
        .delete(`/comments/${commentId}`)
        .then(function(response) {
          toastr.error("Comment deleted");
          that.setState({
            comments: comments
          });
        })
        .catch(function(error) {
          toastr.error("Comment not deleted");
          console.log(error);
        });
    }
  };

  handlerAdded = comment => {
    const comments = this.state.comments;
    comments.unshift(comment);

    this.setState({ comments: comments });
  };

  handlerUpdated = comment => {
    const comments = this.state.comments;
    const idx = lodash.findIndex(comments, { id: comment.id });
    comments[idx] = comment;

    this.setState({ comments });
  };

  handlerToggleEdit = commentId => {
    const comments = this.state.comments;
    const idx = lodash.findIndex(comments, { id: commentId });
    comments[idx].edit = !comments[idx].edit;

    this.setState({ comments });
  };

  render() {
    return (
      <div className="well shadowDepth1">
        <NewComment
          postUrl={this.props.post_url}
          currentUserId={this.props.current_user_id}
          onAdded={this.handlerAdded}
        />

        <hr />
        <List
          currentUserId={this.props.current_user_id}
          comments={this.state.comments}
          onEditComment={this.handlerToggleEdit}
          onDeleteComment={this.deleteComment}
          onUpdated={this.handlerUpdated}
        />
      </div>
    );
  }
}

Comments.propTypes = {
  current_user_id: PropTypes.number,
  comments: PropTypes.array,
  post_url: PropTypes.string
};

export default Comments;
